import { useMutation } from '@apollo/client';
import type { DeleteCustomRoleMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { DeleteCustomRoleDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useDeleteCustomRoleTRPC } from './useDeleteCustomRoleTRPC';

/**
 * Flag-gated wrapper. Consumers ALWAYS import this hook (never the
 * `*TRPC` sibling directly). When the `trpc` flag is on, the call is
 * routed to the tRPC mutation; otherwise it falls back to the legacy
 * Apollo `delete_custom_role` GraphQL mutation.
 */
export const useDeleteCustomRole = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // Apollo mutation — legacy fallback. Mirrors the eviction that used
  // to live inline in `update/Page.tsx`.
  const [deleteCustomRoleGraphQL, graphqlState] = useMutation(
    DeleteCustomRoleDocument,
    {
      update: (cache) => {
        evictField(cache, 'custom_role');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useDeleteCustomRoleTRPC();

  const deleteCustomRole = async (variables: {
    id: string;
  }): Promise<DeleteCustomRoleMutation> => {
    if (trpcEnabled) {
      return trpcMutation.deleteCustomRole(variables);
    }

    const result = await deleteCustomRoleGraphQL({
      variables: {
        filter: {
          Id: { _eq: variables.id },
        },
      },
    });
    if (!result.data) {
      throw new Error('Failed to delete custom role');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      deleteCustomRole,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    deleteCustomRole,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
